import clsx from 'clsx';

import { ResourceKind, ScopeRow } from '@/types/apiKey';
import { INVOKABLE_KINDS, normalizeRow } from '@/utils/apiKeyScope';

import styles from './apiKeysPanel.module.scss';
import { ResourcePicker } from './resourcePicker';


const ROWS: { kind: ResourceKind; label: string }[] = [
  { kind: 'constellation', label: 'Constellations' },
  { kind: 'secret', label: 'Secrets' },
  { kind: 'evaluation', label: 'Evaluations' }
];

type ScopeBuilderProps = {
  rows: ScopeRow[];
  onChange: ( next: ScopeRow[] ) => void;
  disabled?: boolean;
};

export function ScopeBuilder( { rows, onChange, disabled }: ScopeBuilderProps ) {
  const updateRow = ( kind: ResourceKind, patch: Partial<ScopeRow> ) => {
    const next = rows.map( r => {
      if ( r.kind !== kind ) return r;
      return normalizeRow( { ...r, ...patch } );
    } );
    onChange( next );
  };

  return (
    <div className={ styles.scope_builder }>
      <table className={ styles.scope_table }>
        <thead>
          <tr>
            <th>Asset type</th>
            <th>Read</th>
            <th>Invoke</th>
            <th>Write</th>
          </tr>
        </thead>
        <tbody>
          { ROWS.map( ( { kind, label } ) => {
            const row = rows.find( r => r.kind === kind );
            if ( !row ) return null;
            const invokable = INVOKABLE_KINDS.has( kind );
            // Write implies Read, as well as implies Invoke on assets with invoke permissions
            const readLocked = row.write;
            const invokeLocked = row.write && invokable;
            return (
              <tr key={ kind }>
                <td>{ label }</td>
                <td>
                  <input
                    type="checkbox"
                    checked={ row.read }
                    disabled={ disabled || readLocked }
                    onChange={ e => updateRow( kind, { read: e.target.checked } ) }
                    data-testid={ `scope-row-${ kind }-read` }
                  />
                </td>
                <td>
                  { invokable ? (
                    <input
                      type="checkbox"
                      checked={ row.invoke }
                      disabled={ disabled || invokeLocked }
                      onChange={ e => updateRow( kind, { invoke: e.target.checked } ) }
                      data-testid={ `scope-row-${ kind }-invoke` }
                    />
                  ) : (
                    <span className={ styles.muted }>—</span>
                  ) }
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={ row.write }
                    disabled={ disabled }
                    onChange={ e => updateRow( kind, { write: e.target.checked } ) }
                    data-testid={ `scope-row-${ kind }-write` }
                  />
                </td>
              </tr>
            );
          } ) }
        </tbody>
      </table>

      { ROWS.map( ( { kind, label } ) => {
        const row = rows.find( r => r.kind === kind );
        if ( !row ) return null;
        const hasPerm = row.read || row.invoke || row.write;
        if ( !hasPerm ) return null;
        return (
          <div key={ kind } className={ styles.picker_block }>
            <div className={ clsx( styles.picker_label ) }>Select { label.toLowerCase() }</div>
            <ResourcePicker
              kind={ kind }
              selected={ row.targets }
              onChange={ targets => updateRow( kind, { targets } ) }
              disabled={ disabled }
            />
            { row.targets.length === 0 && (
              <div className={ styles.validation_error_inline }>
                Pick at least one { label.slice( 0, -1 ).toLowerCase() } or uncheck the row.
              </div>
            ) }
          </div>
        );
      } ) }
    </div>
  );
}
